<template>
  <div>
    <slot
      :chapters="chapters"
      :change-page="changePage"
      :transition-class="transitionClass"
      :show-prev="showPrev"
      :show-next="showNext"
      :loading-book="loadingBook"
      :error-while-loading="errorWhileLoading"
      :on-chapter-click="onChapterClick"
      :total-pages="totalPages"
      :current-page="currentPage"
      :progress-perc="progressPerc"
      :on-range-change="onRangeChange"
      :on-range-input="onRangeInput"
      :pages-left="pagesLeft"
      :error-message="errorMessage"
      :increase-font-size="increaseFontSize"
      :decrease-font-size="decreaseFontSize"
      :font-size="fontSize"
    ></slot>
  </div>
</template>

<script>
import { storeToRefs } from 'pinia'
import { useBooksProgressStore } from '~/stores/booksProgress'
import { useUserStore } from '~/stores/user'
import { useBooksStore } from '~/stores/books'
import ePub from 'epubjs'

export default {
  props: {
    book: {
      type: Object,
      required: true
    },
    fileUrl: {
      type: String,
      default: ''
    },
    withProgress: {
      type: Boolean,
      default: false
    },
    readerHeight: {
      type: String,
      default: '80vh'
    }
  },
  setup() {
    const userStore = useUserStore()
    const booksProgressStore = useBooksProgressStore()
    const { bookProgress } = storeToRefs(booksProgressStore)
    const { createBookProgress, updateBookProgress } = booksProgressStore
    const booksStore = useBooksStore()
    const { getEbookDownloadUrl } = booksStore

    return {
      userStore,
      bookProgressBySlug: bookProgress,
      createBookProgress,
      updateBookProgress,
      getEbookDownloadUrl
    }
  },
  data() {
    return {
      rendition: null,
      epubBook: null,
      showPrev: false,
      showNext: false,
      loadingBook: true,
      errorWhileLoading: false,
      errorMessage: 'messages.errorOccuredMessage',
      transitionClass: '',
      fetchedFileUrl: '',
      chapters: [],
      locations: [],
      activeChapter: '',
      lastLocation: '',
      currentPage: 0,
      totalPages: 0,
      progressPerc: 0,
      pagesLeft: 0,
      fontSize: 100
    }
  },
  computed: {
    loggedInUser() {
      return this.userStore.loggedInUser
    },
    locationsStorageKey() {
      return this.book.title + '_locations'
    },
    bookProgress() {
      return this.bookProgressBySlug(this.book.slug)
    }
  },
  watch: {
    readerHeight(height) {
      try {
        if (this.rendition) {
          this.rendition.resize('100%', height)
        }
        // eslint-disable-next-line no-empty
      } catch {}
    }
  },
  async mounted() {
    if (!this.book) {
      this.errorWhileLoading = true
      this.loadingBook = false
      return
    }

    if (this.bookProgress && this.bookProgress.cfi) {
      this.lastLocation = this.bookProgress.cfi
    }

    this.fontSize = this.$localStorage.getItem('fontSize') || this.fontSize
    this.errorWhileLoading = false
    this.loadingBook = true

    this.epubBook = ePub()

    await this.openBook()
    this.renderBook()
    this.getChapters()
  },
  beforeUnmount() {
    this.destroyPreview()
  },
  methods: {
    async openBook() {
      try {
        this.loadingBook = true
        if (!this.fetchedFileUrl && !this.fileUrl) {
          await this.getBookFileUrl()
        }

        const file = this.fileUrl || this.fetchedFileUrl

        await this.epubBook.open(file, 'epub')

        if (this.withProgress) {
          this.epubBook.ready.then(() => this.addLocations())
        }
      } catch (error) {
        this.onError(error)
      }
    },

    async getBookFileUrl() {
      try {
        const { fileUrl, isUserAllowToDownload } =
          await this.getEbookDownloadUrl({
            id: this.book.id,
            categoryCode: this.book.categoryCode,
            format: 'ePub',
            language: 'nl-NL',
            isReader: true
          })

        this.fetchedFileUrl =
          fileUrl && fileUrl.includes('eboekhuis.nl')
            ? `/api/download-epub?url=${encodeURIComponent(fileUrl)}`
            : fileUrl

        if (!isUserAllowToDownload) {
          this.errorMessage = 'books.ebookReader.noAccess'
          this.errorWhileLoading = true

          return
        }

        return fileUrl
      } catch (error) {
        this.onError(error)
      }
    },

    renderBook() {
      if (!this._isDestroyed) {
        this.rendition = this.epubBook.renderTo('viewer', {
          width: '100%',
          height: this.readerHeight,
          allowScriptedContent: true
        })

        this.rendition.themes.override('font-family', '"DM Sans", sans-serif')

        if (this.fetchedFileUrl) {
          this.rendition.themes.fontSize(`${this.fontSize}%`)
        }

        this.rendition.on('rendered', (section) => this.onRendered(section))
        this.rendition.on('relocated', (location) => this.onRelocated(location))

        // 'null' check is for https://digi-muse.atlassian.net/browse/BC-4188, we can remove it once fixed
        const notAValidLocation = [
          'null',
          'A JavaScript exception occurred',
          'cfi'
        ]
        const location =
          this.lastLocation &&
          !notAValidLocation.includes(this.lastLocation) &&
          this.fetchedFileUrl
            ? this.lastLocation
            : undefined

        this.rendition.display(location)

        // workaround for not displaying proper page when using bigger fonts
        setTimeout(() => {
          this.rendition.display(location)
        }, 400)
      }
    },
    onRendered() {
      this.loadingBook = false
      document.addEventListener('keydown', this.keyListener, false)
    },

    onRelocated(location) {
      this.pagesLeft =
        location.start.displayed.page === 1
          ? location.start.displayed.total
          : location.end.displayed.total - location.end.displayed.page + 2

      this.lastLocation = location.end.cfi
      this.updateArrows(location)
      if (this.withProgress) {
        this.setCurrentPage(location.start.cfi)
        this.updateProgress()
      }
    },

    getChapters() {
      this.epubBook.loaded.navigation.then(({ toc }) => {
        this.chapters = toc
      })
    },

    async addLocations() {
      const stored = this.$localStorage.getItem(this.locationsStorageKey)

      if (stored) {
        this.locations = await this.epubBook.locations.load(stored)
      } else {
        this.locations = await this.epubBook.locations.generate(1600)

        this.$localStorage.addItem(
          this.locationsStorageKey,
          this.epubBook.locations.save(),
          1
        )
      }

      this.totalPages = this.epubBook.locations.total

      if (this.bookProgress && this.bookProgress.currentPageNumber) {
        this.currentPage = this.bookProgress.currentPageNumber
        this.progressPerc = this.bookProgress.ebookPercentage
      } else {
        this.setCurrentPage(this.lastLocation)
      }
    },

    setCurrentPage(location) {
      const percent = this.epubBook.locations.percentageFromCfi(location)
      this.progressPerc = Math.round(percent * 100)

      this.currentPage = Math.round(this.epubBook.locations.total * percent)
    },

    onRangeChange(event) {
      const value = parseInt(event.target.value)
      this.progressPerc = value
      const cfi = this.epubBook.locations.cfiFromPercentage(value / 100)
      if (this.rendition) {
        this.rendition.display(value === 0 ? undefined : cfi)
      }
    },

    onRangeInput(event) {
      this.progressPerc = parseInt(event.target.value)
    },

    keyListener(event) {
      if (event.key === 'ArrowLeft') {
        this.changePage('prev')
      }

      if (event.key === 'ArrowRight') {
        this.changePage('next')
      }
    },

    async updateProgress() {
      try {
        const bookProgress = this.bookProgressBySlug(this.book.slug)

        if (
          !this.fetchedFileUrl ||
          this.currentPage === bookProgress?.currentPageNumber ||
          !this.totalPages
        ) {
          return
        }

        const progressData = {
          cfi: this.lastLocation,
          currentPageNumber: this.currentPage,
          totalRenderedPageNumber: this.totalPages
        }

        if (!bookProgress) {
          await this.createBookProgress({
            ...progressData,
            bookSlug: this.book.slug,
            userId: this.loggedInUser.id
          })
        } else {
          await this.updateBookProgress({
            ...progressData,
            id: bookProgress.id
          })
        }
      } catch (error) {
        this.$toast.error(this.$label('books.progress.unableToSaveError'))
        this.$sentryCaptureException(error)
      }
    },

    updateArrows(location) {
      this.showNext = !location.atEnd
      this.showPrev = !location.atStart
    },

    addPageTransition(direction) {
      this.transitionClass = `viewer-transition--${direction}`

      setTimeout(() => {
        this.transitionClass = ''
      }, 550)
    },

    changePage(direction) {
      if (!this.rendition || this.errorWhileLoading || this.loadingBook) return

      this.transitionClass = ''

      if (this.showPrev && direction === 'prev') {
        this.addPageTransition(direction)
        this.rendition.prev()
      }

      if (this.showNext && direction === 'next') {
        this.addPageTransition(direction)
        this.rendition.next()
      }
    },

    onChapterClick(chapter) {
      this.addPageTransition('next')
      if (this.rendition) {
        this.rendition.display(chapter.href)
      }
    },

    increaseFontSize() {
      if (this.fontSize < 200 && this.rendition) {
        this.fontSize += 10
        this.rendition.themes.fontSize(`${this.fontSize}%`)
        this.rendition.display(this.lastLocation)
        this.saveFontSize()
      }
    },

    decreaseFontSize() {
      if (this.fontSize > 60 && this.rendition) {
        this.fontSize -= 10
        this.rendition.themes.fontSize(`${this.fontSize}%`)
        this.rendition.display(this.lastLocation)
        this.saveFontSize()
      }
    },

    saveFontSize() {
      this.$localStorage.addItem('fontSize', this.fontSize, 365)
    },

    onError(error) {
      this.errorWhileLoading = true
      this.$sentryCaptureException(error)
    },

    destroyPreview() {
      try {
        this.showPrev = false
        this.showNext = false
        this.errorWhileLoading = false

        document.removeEventListener('keydown', this.keyListener, false)
        if (this.rendition) {
          this.rendition.destroy()
        }
        // eslint-disable-next-line no-empty
      } catch {}
    }
  }
}
</script>
